import AppIconButton from "../form/AppIconButton";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../form";
import { useMaterialUIController } from "../../context";
import { getReportChatShell } from "../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import {
  openapiCatalogService,
  type OpenApiCatalogSummary,
  type OpenApiTagSummary,
} from "../../services/openapiCatalogService";

export type OpenApiSelection = {
  catalogId: string;
  catalogName: string;
  tag?: string;
};

type Props = {
  value: OpenApiSelection | null;
  onChange: (next: OpenApiSelection | null) => void;
  disabled?: boolean;
  iconColor?: string;
  /** Controlled open state; when set, the parent owns opening/closing. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render only the panel (e.g. when opened from the composer tools palette). */
  hideTrigger?: boolean;
};

export default function ChatOpenApiPicker({ value, onChange, disabled, iconColor, open: openProp, onOpenChange, hideTrigger }: Props) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const shell = getReportChatShell(theme);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const [catalogs, setCatalogs] = useState<OpenApiCatalogSummary[]>([]);
  const [tags, setTags] = useState<OpenApiTagSummary[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState("");
  const [draftCatalog, setDraftCatalog] = useState<OpenApiCatalogSummary | null>(null);

  const loadCatalogs = useCallback(async () => {
    setLoadingCatalogs(true);
    setError(null);
    try {
      const list = await openapiCatalogService.listChatCatalogs();
      setCatalogs(list);
    } catch (e) {
      console.error("Failed to load OpenAPI catalogs", e);
      setError(t("chat.openApiPicker.loadCatalogsFailed"));
      setCatalogs([]);
    } finally {
      setLoadingCatalogs(false);
    }
  }, [t]);

  const loadTags = useCallback(
    async (catalogId: string) => {
      setLoadingTags(true);
      setError(null);
      try {
        const list = await openapiCatalogService.listTags(catalogId);
        setTags(list);
      } catch (e) {
        console.error("Failed to load OpenAPI tags", e);
        setError(t("chat.openApiPicker.loadTagsFailed"));
        setTags([]);
      } finally {
        setLoadingTags(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (!open) return;
    void loadCatalogs();
    if (value?.catalogId) {
      const match: OpenApiCatalogSummary = {
        id: value.catalogId,
        name: value.catalogName || value.catalogId,
      };
      setDraftCatalog(match);
      void loadTags(value.catalogId);
    } else {
      setDraftCatalog(null);
      setTags([]);
    }
    setTagFilter("");
  }, [open, value?.catalogId, value?.catalogName, loadCatalogs, loadTags]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest(
          ".MuiAutocomplete-popper, .MuiPopover-root, .MuiModal-root, [role='listbox']"
        )
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, setOpen]);

  const filteredTags = tags.filter((row) => {
    const q = tagFilter.trim().toLowerCase();
    if (!q) return true;
    return (
      row.tag.toLowerCase().includes(q) ||
      (row.sampleTools || []).some((tool) => tool.toLowerCase().includes(q))
    );
  });

  const selectCatalog = (catalog: OpenApiCatalogSummary) => {
    setDraftCatalog(catalog);
    setTagFilter("");
    onChange({ catalogId: catalog.id, catalogName: catalog.name });
    void loadTags(catalog.id);
  };

  const selectTag = (tag: string) => {
    if (!draftCatalog) return;
    onChange({
      catalogId: draftCatalog.id,
      catalogName: draftCatalog.name,
      tag,
    });
    setOpen(false);
  };

  return (
    <Box ref={rootRef} sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {!hideTrigger && (
        <AppIconButton
          size="small"
          disabled={disabled}
          busyIgnore
          title={t("chat.tooltips.openapi")}
          onClick={() => setOpen(!open)}
          sx={custom.components.chatIndexPicker.triggerButton(iconColor)}
          aria-label={t("chat.openApiPicker.open")}
          color={value || open ? "primary" : "default"}
        >
          <HubRoundedIcon fontSize="small" />
        </AppIconButton>
      )}

      {open ? (
        <Paper
          elevation={8}
          sx={{
            ...custom.components.chatIndexPicker.popoverPaper(shell, controller.darkMode),
            position: "absolute",
            bottom: "100%",
            left: 0,
            mb: 1,
            zIndex: (theme) => theme.zIndex.modal,
            width: 400,
            maxWidth: "min(92vw, 400px)",
            maxHeight: "min(70vh, 520px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="subtitle2" sx={custom.components.chatIndexPicker.title(shell.assistantText)}>
            {t("chat.openApiPicker.title")}
          </Typography>
          <Typography variant="caption" sx={custom.components.chatIndexPicker.hint(shell.muted)}>
            {t("chat.openApiPicker.hint")}
          </Typography>

          {error ? (
            <Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
              {error}
            </Typography>
          ) : null}

          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
            {t("chat.openApiPicker.catalogLabel")}
          </Typography>
          {loadingCatalogs ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={custom.components.chatIndexPicker.chipsRow}>
              {catalogs.map((catalog) => (
                <Chip
                  key={catalog.id}
                  size="small"
                  label={catalog.name}
                  color={draftCatalog?.id === catalog.id ? "primary" : "default"}
                  variant={draftCatalog?.id === catalog.id ? "filled" : "outlined"}
                  clickable
                  onClick={() => selectCatalog(catalog)}
                />
              ))}
              {catalogs.length === 0 && !loadingCatalogs && (
                <Typography variant="caption" color="text.secondary">
                  {t("chat.openApiPicker.noCatalogs")}
                </Typography>
              )}
            </Stack>
          )}

          {draftCatalog ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                {t("chat.openApiPicker.tagLabel")}
              </Typography>
              <AppTextField
                size="small"
                fullWidth
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                placeholder={t("chat.openApiPicker.tagSearch")}
                sx={{ mb: 1 }}
              />
              {loadingTags ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <List dense disablePadding sx={{ overflow: "auto", flex: 1, minHeight: 120, maxHeight: 240 }}>
                  {filteredTags.map((row) => (
                    <ListItemButton
                      key={row.tag}
                      selected={value?.tag === row.tag}
                      onClick={() => selectTag(row.tag)}
                    >
                      <ListItemText
                        primary={row.tag}
                        secondary={
                          row.sampleTools && row.sampleTools.length > 0
                            ? row.sampleTools.slice(0, 3).join(", ")
                            : row.operationCount != null
                              ? t("chat.openApiPicker.operationCount", { count: row.operationCount })
                              : undefined
                        }
                        primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                  {filteredTags.length === 0 && (
                    <Box sx={{ px: 1, py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("chat.openApiPicker.noTags")}
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </>
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={custom.components.chatIndexPicker.actions}>
            <AppButton
              size="small"
              variant="outlined"
              onClick={() => {
                onChange(null);
                setDraftCatalog(null);
                setTags([]);
                setOpen(false);
              }}
            >
              {t("chat.openApiPicker.clear")}
            </AppButton>
            <AppButton size="small" variant="contained" onClick={() => setOpen(false)}>
              {t("common.save")}
            </AppButton>
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
